import { extendTheme } from '@chakra-ui/react';
import { mode } from '@chakra-ui/theme-tools';
import { THEME_COLORS } from './config';

// Define the color palette based on our config
const colors = {
  brand: {
    50: '#e6e6ff',
    100: '#c4c4ff',
    200: '#a1a1ff',
    300: '#7e7eff',
    400: '#7678ed', // Our accent color
    500: '#5c5dc7', // Darker accent
    600: '#4848a0',
    700: '#353579',
    800: '#232353',
    900: '#121226',
  },
  dark: {
    bg: THEME_COLORS.background,
    card: THEME_COLORS.cardBackground,
    border: '#30304e',
    textPrimary: THEME_COLORS.text,
    textSecondary: THEME_COLORS.secondaryText,
  },
  light: {
    bg: '#f7f9fc',
    card: '#ffffff',
    border: '#e2e8f0',
    textPrimary: '#1a202c',
    textSecondary: '#4a5568',
  },
  accent: {
    green: THEME_COLORS.success,
    purple: THEME_COLORS.accent,
    red: THEME_COLORS.error,
    yellow: THEME_COLORS.warning,
  },
};

// Define the theme
const theme = extendTheme({
  colors,
  fonts: {
    heading: '"Inter", sans-serif',
    body: '"Inter", sans-serif',
  },
  config: {
    initialColorMode: 'dark',
    useSystemColorMode: false,
  },
  styles: {
    global: (props) => ({
      body: {
        bg: mode('light.bg', 'dark.bg')(props),
        color: mode('light.textPrimary', 'dark.textPrimary')(props),
      },
      '::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '::-webkit-scrollbar-track': {
        background: mode('rgba(0, 0, 0, 0.05)', 'rgba(0, 0, 0, 0.1)')(props),
      },
      '::-webkit-scrollbar-thumb': {
        background: mode('rgba(0, 0, 0, 0.2)', 'rgba(255, 255, 255, 0.1)')(props),
        borderRadius: '4px',
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: mode('rgba(0, 0, 0, 0.3)', 'rgba(255, 255, 255, 0.2)')(props),
      },
    }),
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: 'semibold',
        borderRadius: 'md',
      },
      variants: {
        solid: (props) => ({
          bg: 'brand.400',
          color: 'white',
          _hover: {
            bg: 'brand.500',
          },
        }),
        outline: (props) => ({
          borderColor: 'brand.400',
          color: mode('brand.500', 'brand.400')(props),
          _hover: {
            bg: mode('rgba(118, 120, 237, 0.05)', 'rgba(118, 120, 237, 0.1)')(props),
          },
        }),
        ghost: (props) => ({
          color: mode('light.textPrimary', 'dark.textPrimary')(props),
          _hover: {
            bg: mode('rgba(0, 0, 0, 0.05)', 'rgba(255, 255, 255, 0.1)')(props),
          },
        }),
        success: (props) => ({
          bg: mode('rgba(0, 255, 157, 0.1)', 'rgba(0, 255, 157, 0.2)')(props),
          color: mode('green.600', 'accent.green')(props),
          _hover: {
            bg: mode('rgba(0, 255, 157, 0.2)', 'rgba(0, 255, 157, 0.3)')(props),
          },
        }),
        error: (props) => ({
          bg: mode('rgba(255, 0, 102, 0.1)', 'rgba(255, 0, 102, 0.2)')(props),
          color: mode('red.600', 'accent.red')(props),
          _hover: {
            bg: mode('rgba(255, 0, 102, 0.2)', 'rgba(255, 0, 102, 0.3)')(props),
          },
        }),
        warning: (props) => ({
          bg: mode('rgba(255, 204, 0, 0.1)', 'rgba(255, 204, 0, 0.2)')(props),
          color: mode('yellow.600', 'accent.yellow')(props),
          _hover: {
            bg: mode('rgba(255, 204, 0, 0.2)', 'rgba(255, 204, 0, 0.3)')(props),
          },
        }),
      },
    },
    Card: {
      baseStyle: (props) => ({
        bg: mode('light.card', 'dark.card')(props),
        borderRadius: 'lg',
        boxShadow: mode('sm', 'none')(props),
        borderWidth: '1px',
        borderColor: mode('light.border', 'dark.border')(props),
        overflow: 'hidden',
      }),
    },
    Heading: {
      baseStyle: (props) => ({
        color: mode('light.textPrimary', 'dark.textPrimary')(props),
        fontWeight: 'bold',
      }),
    },
    Text: {
      baseStyle: (props) => ({
        color: mode('light.textPrimary', 'dark.textPrimary')(props),
      }),
      variants: {
        secondary: (props) => ({
          color: mode('light.textSecondary', 'dark.textSecondary')(props),
        }),
      },
    },
    Input: {
      baseStyle: (props) => ({
        field: {
          bg: mode('white', 'dark.bg')(props),
          borderColor: mode('light.border', 'dark.border')(props),
          _hover: {
            borderColor: 'brand.400',
          },
          _focus: {
            borderColor: 'brand.400',
            boxShadow: '0 0 0 1px rgba(118, 120, 237, 0.6)',
          },
        },
      }),
      variants: {
        filled: (props) => ({
          field: {
            bg: mode('gray.50', 'rgba(0, 0, 0, 0.2)')(props),
            _hover: {
              bg: mode('gray.100', 'rgba(0, 0, 0, 0.3)')(props),
            },
            _focus: {
              bg: mode('gray.100', 'rgba(0, 0, 0, 0.3)')(props),
            },
          },
        }),
      },
    },
    Select: {
      baseStyle: (props) => ({
        field: {
          bg: mode('white', 'dark.bg')(props),
          borderColor: mode('light.border', 'dark.border')(props),
          _hover: {
            borderColor: 'brand.400',
          },
          _focus: {
            borderColor: 'brand.400',
            boxShadow: '0 0 0 1px rgba(118, 120, 237, 0.6)',
          },
        },
      }),
    },
    Checkbox: {
      baseStyle: (props) => ({
        control: {
          borderColor: mode('light.border', 'dark.border')(props),
          _checked: {
            bg: 'brand.400',
            borderColor: 'brand.400',
          },
        },
      }),
    },
    Badge: {
      baseStyle: {
        borderRadius: 'md',
        fontWeight: 'medium',
        textTransform: 'none',
      },
      variants: {
        success: (props) => ({
          bg: mode('green.100', 'rgba(0, 255, 157, 0.2)')(props),
          color: mode('green.600', 'accent.green')(props),
        }),
        error: (props) => ({
          bg: mode('red.100', 'rgba(255, 0, 102, 0.2)')(props),
          color: mode('red.600', 'accent.red')(props),
        }),
        warning: (props) => ({
          bg: mode('yellow.100', 'rgba(255, 204, 0, 0.2)')(props),
          color: mode('yellow.600', 'accent.yellow')(props),
        }),
        info: (props) => ({
          bg: mode('blue.100', 'rgba(118, 120, 237, 0.2)')(props),
          color: mode('blue.600', 'brand.400')(props),
        }),
      },
    },
    Table: {
      variants: {
        simple: (props) => ({
          th: {
            borderColor: mode('light.border', 'dark.border')(props),
            color: mode('light.textSecondary', 'dark.textSecondary')(props),
          },
          td: {
            borderColor: mode('light.border', 'dark.border')(props),
          },
        }),
      },
    },
    Modal: {
      baseStyle: (props) => ({
        dialog: {
          bg: mode('light.card', 'dark.card')(props),
          borderRadius: 'lg',
          borderWidth: '1px',
          borderColor: mode('light.border', 'dark.border')(props),
        },
        header: {
          borderBottomWidth: '1px',
          borderColor: mode('light.border', 'dark.border')(props),
        },
        footer: {
          borderTopWidth: '1px',
          borderColor: mode('light.border', 'dark.border')(props),
        },
      }),
    },
    Tabs: {
      variants: {
        line: (props) => ({
          tab: {
            color: mode('gray.600', 'gray.400')(props),
            _selected: {
              color: mode('brand.500', 'brand.400')(props),
              borderColor: mode('brand.500', 'brand.400')(props),
            },
          },
        }),
        enclosed: (props) => ({
          tab: {
            color: mode('gray.600', 'gray.400')(props),
            bg: mode('gray.50', 'whiteAlpha.50')(props),
            _selected: {
              bg: mode('white', 'dark.card')(props),
              color: mode('brand.500', 'brand.400')(props),
              borderColor: mode('light.border', 'dark.border')(props),
              borderBottomColor: mode('white', 'dark.card')(props),
            },
          },
        }),
      },
    },
  },
});

export default theme;